import {
  type StreamChannelName,
  streamServerMessageSchema,
} from "@redotech/effect-api-shared/models/stream-protocol";
import { type AnyStreamChannelSchemaDefinition } from "@redotech/effect-api-stream-schemas";

export interface SubscriptionHandlerRecord {
  readonly id: string;
  readonly onError?: (message: string) => void;
  readonly onEvent: (payload: unknown) => void;
  readonly onInitial?: (payload: unknown) => void;
  readonly onSubscribed?: () => void;
  readonly onUnsubscribed?: () => void;
}

export interface LogicalSubscriptionRecord {
  readonly channel: StreamChannelName;
  readonly definition: AnyStreamChannelSchemaDefinition;
  readonly handlers: Map<string, SubscriptionHandlerRecord>;
  readonly input: unknown;
  readonly inputJson: string;
  lastInitialPayload?: unknown;
  subscriptionKey: string;
}

export function getSubscriptionKey(
  channel: StreamChannelName,
  inputJson: string,
): string {
  return `${channel}:${inputJson}`;
}

export function parseEventData(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}

export function notifySubscriptionError(
  subscription: LogicalSubscriptionRecord,
  message: string,
): void {
  for (const handler of subscription.handlers.values()) {
    handler.onError?.(message);
  }
}

export function handleServerMessage(
  raw: unknown,
  subscriptions: Map<string, LogicalSubscriptionRecord>,
): void {
  const parsedMessage = streamServerMessageSchema.safeParse(raw);
  if (!parsedMessage.success) {
    return;
  }

  const message = parsedMessage.data;
  const subscription = subscriptions.get(message.subscriptionKey ?? "");
  if (subscription == null) {
    return;
  }

  if (message._tag === "subscribed") {
    for (const handler of subscription.handlers.values()) {
      handler.onSubscribed?.();
    }
    return;
  }

  if (message._tag === "unsubscribed") {
    for (const handler of subscription.handlers.values()) {
      handler.onUnsubscribed?.();
    }
    return;
  }

  if (message._tag === "error") {
    for (const handler of subscription.handlers.values()) {
      handler.onError?.(message.message);
    }
    return;
  }

  if (message._tag === "initial") {
    const parsedPayload = subscription.definition.initialSchema.safeParse(
      message.payload,
    );
    if (!parsedPayload.success) {
      return;
    }
    subscription.lastInitialPayload = parsedPayload.data;
    for (const handler of subscription.handlers.values()) {
      handler.onInitial?.(parsedPayload.data);
    }
    return;
  }

  const parsedPayload = subscription.definition.eventSchema.safeParse(
    message.payload,
  );
  if (!parsedPayload.success) {
    return;
  }
  for (const handler of subscription.handlers.values()) {
    handler.onEvent(parsedPayload.data);
  }
}
